/**
 * Krok REP7 — VYSKA BLOKU BEZ PROHLIZECE.
 *
 * ==== PROC TENHLE MODUL VUBEC JE ====
 *
 * Krok REP4 zavedl mez „nad patickou nejvyse 25 mm volneho mista", ale
 * dodrzet ji umel jen autofit v `layout.ts`, ktery meri v Chromiu. Produkcni
 * export PDF bezi ve workeru pres Gotenberg a Playwright tam ZAMERNE neni
 * (ROZHODNUTI REP2-5) — takze v produkci mez nikdo nehlidal a eval 30. 9. 2026
 * nasel 136 stran s prazdnym mistem a dve pretecene (`evals/results/2026-09-30-1630-pdf.md`).
 *
 * Sazba proto od kroku REP7 nestoji na mereni, ale na VYPOCTU: kazdy blok umi
 * rict, kolik milimetru zabere, a `paginate.ts` z toho slozi strany. Pocita se
 * ze stejneho CSS, jake dokument opravdu dostane (`css.ts`), a ze SKUTECNYCH
 * sirek znaku pouziteho pisma (`font-metrics.ts`) — ne z prumerne sirky „od oka".
 *
 * ==== SMER CHYBY JE VZDY NA STRANU VETSI VYSKY ====
 *
 * Kdyz se odhad splete, musi se splest tak, aby strana zustala radsi prazdnejsi,
 * nez aby pretekla: `.page` ma `overflow:hidden`, takze pretecena strana
 * TISE URIZNE text (CLAUDE.md §10 — prazdne misto je vada sazby, uriznuty
 * odstavec je vada dokumentu). Proto se:
 *   - radky lamou jen na mezerach (prohlizec umi zlomit i za pomlckou, tedy
 *     nikdy nepotrebuje radku VIC, nez napocitame),
 *   - kerning se nezapocitava (pismo umi par tisicin em ubrat, ne pridat),
 *   - a `paginate.ts` si nad vysledek jeste bere rezervu.
 *
 * Presnost hlida `report-v5-estimate.spec.ts`, ktery tytez bloky zmeri
 * v Chromiu a porovna blok po bloku.
 */
import { t } from '@reos/i18n';
import { brandInitials, safeLogoUrl } from '../brand.js';
import { gaugeSvg, renderChartSvg } from './charts.js';
import { FONT_ADVANCE, fallbackAdvance } from './font-metrics.js';
import { BODY_LINE_HEIGHT, FLAT_QUESTION_LINE_HEIGHT, H2_LINE_HEIGHT, PT } from './type-scale.js';
// ------------------------------------------------------------- jednotky
/** Milimetru na jeden typograficky bod. */
export const MM_PER_PT = 25.4 / 72;
/** A4 a odsazeni strany (`css.ts`: `.page{width:210mm;height:297mm;padding:11mm 15mm 15mm}`). */
export const PAGE_HEIGHT_MM = 297;
const PAGE_PADDING_TOP_MM = 11;
const PAGE_PADDING_BOTTOM_MM = 15;
/** Sirka sazebniho obrazce: 210 − 2 × 15 mm. */
export const CONTENT_WIDTH_MM = 180;
/** Zakladni radkovani dokumentu (`body{line-height}`, viz `type-scale.ts`). */
const LINE_HEIGHT = BODY_LINE_HEIGHT;
/**
 * Jak uzky radek se pri lomeni predpoklada proti skutecne sirce. Kerning
 * a zaokrouhleni sazby na jednotky rozvrzeni umi u dlouheho radku par desetin
 * milimetru posunout — a kdyz to vyjde tesne, prohlizec zlomi o slovo driv,
 * nez rikal vypocet. Jedno procento rezervy z toho udela chybu na stranu
 * VETSI vysky (radek navic), coz je bezpecny smer (viz hlavicka).
 */
const TEXT_SAFETY = 0.997;
/** Tloustka linky 1 px pri 96 dpi. */
const PX_MM = 25.4 / 96;
// ------------------------------------------------------------- text
/** Sirka retezce v em (1 = jedna sirka pisma). Kerning se nezapocitava — viz hlavicka. */
export function textWidthEm(value, face) {
    const table = FONT_ADVANCE[face];
    let total = 0;
    for (const char of value)
        total += table[char] ?? fallbackAdvance(face);
    return total / 1000;
}
/** Sirka retezce v mm pri dane velikosti pisma. */
function textWidthMm(value, sizePt, face) {
    return textWidthEm(value, face) * sizePt * MM_PER_PT;
}
/**
 * Kolik radku zabere text v dane sirce. Lame se JEN na beznych mezerach:
 * prohlizec ma zlomu vic (za pomlckou, za lomitkem), takze skutecny pocet
 * radku je nejvyse tenhle — chyba jde na stranu vetsi vysky (viz hlavicka).
 */
export function lineCount(value, widthMm, sizePt, face) {
    const words = value.split(' ').filter((word) => word !== '');
    if (words.length === 0)
        return 1;
    const spaceMm = textWidthMm(' ', sizePt, face);
    const limit = widthMm * TEXT_SAFETY;
    let lines = 1;
    let used = 0;
    for (const word of words) {
        const wordMm = textWidthMm(word, sizePt, face);
        if (used === 0) {
            used = wordMm;
            continue;
        }
        if (used + spaceMm + wordMm <= limit) {
            used += spaceMm + wordMm;
            continue;
        }
        lines += 1;
        used = wordMm;
    }
    return lines;
}
/** Vyska odstavce (bez okraju): pocet radku × radkovani. */
function textBlockMm(value, widthMm, sizePt, face, lineHeight = LINE_HEIGHT) {
    return lineCount(value, widthMm, sizePt, face) * sizePt * lineHeight * MM_PER_PT;
}
/** Jeden radek daneho pisma (pro prvky, ktere se nelamou). */
function singleLineMm(sizePt, lineHeight = LINE_HEIGHT) {
    return sizePt * lineHeight * MM_PER_PT;
}
/** Text s durazem -> jeden retezec; tucne behy jsou sirsi, proto se meri zvlast. */
function richWidthEm(text, base, bold) {
    let total = 0;
    for (const run of text) {
        if (typeof run === 'string')
            total += textWidthEm(run, base);
        else if ('b' in run)
            total += textWidthEm(run.b, bold);
        else
            total += textWidthEm(run.em, base);
    }
    return total;
}
/**
 * Radky textu s durazem. Lame se nad SLOZENYM retezcem, ale sirka se bere
 * z nejsirsiho rezu, ktery se v nem vyskytuje — tucny beh nikdy nezuzi radek.
 */
function richLineCount(text, widthMm, sizePt) {
    const plain = text.map((run) => (typeof run === 'string' ? run : 'b' in run ? run.b : run.em)).join('');
    const hasBold = text.some((run) => typeof run !== 'string' && 'b' in run);
    if (!hasBold)
        return lineCount(plain, widthMm, sizePt, 'sans400');
    // Bezpecny smer: sirsi pismo -> vic radku.
    const ratio = richWidthEm(text, 'sans400', 'sans600') / Math.max(textWidthEm(plain, 'sans400'), 0.001);
    return lineCount(plain, widthMm / ratio, sizePt, 'sans400');
}
function richTextMm(text, widthMm, sizePt, lineHeight = LINE_HEIGHT) {
    return richLineCount(text, widthMm, sizePt) * sizePt * lineHeight * MM_PER_PT;
}
/**
 * Vyska bezne hlavicky `.run` vcetne jejiho spodniho okraje. Nejvyssi prvek
 * radku rozhoduje: u klientskeho dokumentu logo (7 mm), u podrobneho cislo
 * kapitoly (11pt serif), a kdyz kancelar logo nema, ctverec s iniciálami
 * (`.bm{height:9mm}`).
 */
export function runHeaderMm(ctx) {
    const hasLogo = safeLogoUrl(ctx.brand?.logoUrl) !== null;
    const flat = isFlat(ctx);
    const markMm = hasLogo
        ? flat
            ? 8
            : ctx.audience === 'klient'
                ? 7
                : 5.5
        : brandInitials(ctx.brand?.officeName ?? null) === null
            ? 0
            : 9;
    const codeMm = ctx.audience === 'podrobny' ? singleLineMm(PT.runCode) : 0;
    /**
     * Krok REPB1: u bytu je v zahlavi VELKY nadpis sekce (15pt serif) a pod nim
     * linka (`.run{padding-bottom:3mm;border-bottom:1px;margin-bottom:7mm}`).
     */
    const titleMm = singleLineMm(flat ? PT.runTitleFlat : PT.runTitle);
    const below = flat ? 3 + PX_MM + 7 : 6;
    return Math.max(markMm, codeMm, titleMm, singleLineMm(PT.runLabel)) + below;
}
/** Krok REPB1: sazi se dokument bytu? (Rozhoduje model, stejne jako v sablone.) */
function isFlat(ctx) {
    return ctx.model.segment === 'flat';
}
/**
 * Krok REPB1 — DVE DLAZDICE VEDLE SEBE (`.kpis.two` v predloze bytu). Plati
 * jen u bytu: u pozemku a domu by to prestavelo schvalene strany. Pravidlo
 * zije TADY a sablona ho cte odsud — dve kopie by znamenaly, ze se meri jina
 * mrizka, nez se vysazi.
 */
export function kpisTwoColumns(ctx, count) {
    return ctx.model.segment === 'flat' && count === 2;
}
/** Velikost pisma paticky (`css.ts`: `.fol`, viz `type-scale.ts`: `PT.footer`). */
const FOOTER_PT = PT.footer;
/**
 * Vyska paticky `.fol` vcetne jejiho horniho okraje
 * (`margin-top:3mm;padding-top:2mm;border-top:1px;font-size:7.4pt`).
 *
 * POZOR: pocita s JEDNIM radkem. Paticka, ktera se zalomi, posune mez obsahu
 * o 4 mm na kazde strane dokumentu — proto `footerMarkFits` nize.
 */
export const FOOTER_MM = 3 + 2 + PX_MM + singleLineMm(FOOTER_PT);
/**
 * Nejmensi mezera mezi sousednimi prvky paticky. `.fol` je `space-between`,
 * takze pri nulovem volnem miste se texty dotknou a splynou v jeden radek
 * (merene na demo kancelari: kontaktni radek 127,1 mm + znacka 42,8 mm +
 * cislo strany 5,4 mm = 175,2 ze 180 mm, tedy 2,4 mm na mezeru). Dve
 * desetiny toho uz neni mezera, ale preklep.
 */
const FOOTER_GAP_MM = 2;
/**
 * Krok BRAND1 — VEJDE SE ZNACKA PRODUKTU DO PATICKY NA JEDEN RADEK?
 *
 * Paticka nese tri veci vedle sebe: kontaktni radek kancelare, znacku
 * produktu a cislo strany. Kontaktni radek je ZAKAZNICKY UDAJ (jmeno, adresa,
 * telefon, web) a u delsi kancelare, nez je demo, uz na znacku misto neni.
 * Prohlizec by paticku zalomil na dva radky; `FOOTER_MM` ale pocita s jednim,
 * takze by se mez obsahu posunula a `.page{overflow:hidden}` by na dotcenych
 * stranach TISE URIZL text (hlavicka tohoto modulu: chyba vzdy na stranu
 * vetsi vysky). Sablona si proto vybere nejdelsi variantu znacky, ktera se
 * vejde, a kdyz se nevejde zadna, znacku vynecha — dokument je prednejsi.
 */
export function footerMarkFits(contactLine, mark, pageLabel, widthMm = CONTENT_WIDTH_MM) {
    const used = textWidthMm(contactLine, FOOTER_PT, 'sans400') +
        textWidthMm(mark, FOOTER_PT, 'sans400') +
        textWidthMm(pageLabel, FOOTER_PT, 'sans400');
    return used + FOOTER_SYMBOL_MM + FOOTER_SYMBOL_GAP_MM + 2 * FOOTER_GAP_MM <= widthMm;
}
/**
 * Krok BRAND2 — MALY SYMBOL ZNACKY V PATICCE (manual str. 8: paticka je
 * „[kancelář] / [kontakt makléře] · Powered by Makléřov" s malym symbolem).
 *
 * Je to GRAFIKA, takze ji `textWidthMm` nezmeri — a presto zabira sirku na
 * temze radku. Bez techto dvou konstant by vypocet `footerMarkFits` lhal
 * o 3,4 mm a u kancelare na hrane by se paticka zalomila (presne vada, kvuli
 * ktere kontrola sirky v BRAND1 vznikla).
 *
 * Velikost je VELIKOST PISMA paticky (7,4 pt), ne jeji radkova vyska: obrazek
 * vyssi nez radek by zvetsil radkovy box, `FOOTER_MM` by zacal lhat o vysce
 * a mez obsahu by se posunula na kazde strane. Symbol je ctvercovy, takze je
 * sirka shodna s vyskou.
 */
export const FOOTER_SYMBOL_MM = Number((FOOTER_PT * MM_PER_PT).toFixed(2));
export const FOOTER_SYMBOL_GAP_MM = 1;
/**
 * Krok REPORT1B — VYSKA KRUHU VERDIKTU VCETNE SLOVA POD NIM (`.vwrap`, manual
 * str. 11: „Barevné stavy vždy doplnit slovem"). Kruh 11 mm + mezera 1 mm +
 * jeden radek slova (`.vword`, `PT.labelSmall`) — viz `css.ts`.
 */
const VERDICT_MARK_MM = 11 + 1 + singleLineMm(PT.labelSmall);
/** Kolik mm zbyva na obsah `main` jedne strany kapitoly. */
export function availableContentMm(ctx) {
    return (PAGE_HEIGHT_MM - PAGE_PADDING_TOP_MM - PAGE_PADDING_BOTTOM_MM - runHeaderMm(ctx) - FOOTER_MM);
}
/**
 * Nadpis kapitoly (`h2`): 18pt serif, `padding-top:4mm`, `margin-bottom:5mm`,
 * `max-width:170mm`. `continued` = druha a dalsi kapitola na tetez strane,
 * ktera se od predchoziho obsahu odrazi (`h2.cont`, viz `css.ts`).
 */
export function headingMm(text, continued) {
    const lines = lineCount(text, 170, PT.h2, 'serif600');
    return 4 + lines * PT.h2 * H2_LINE_HEIGHT * MM_PER_PT + 5 + (continued ? HEADING_CONT_GAP_MM : 0);
}
/** Odsazeni druheho a dalsiho nadpisu kapitoly na strane (`css.ts`: `h2.cont`). */
export const HEADING_CONT_GAP_MM = 8;
// ------------------------------------------------------------- bloky
/** Vyska SVG grafu: `viewBox` urcuje pomer stran, sirka je sirka sloupce. */
function chartSvgMm(svg, widthMm) {
    const match = /viewBox="0 0 ([\d.]+) ([\d.]+)"/.exec(svg);
    if (match === null)
        return 0;
    const vbWidth = Number(match[1]);
    const vbHeight = Number(match[2]);
    if (!Number.isFinite(vbWidth) || vbWidth <= 0)
        return 0;
    return (widthMm * vbHeight) / vbWidth;
}
/** Pevne vysky obrazku z `css.ts` podle varianty bloku `figure`. */
function figureImageMm(variant, audience, flat = false) {
    /**
     * Krok REPB1 — vysky fotek v reportu bytu (predloha): fotka pres celou
     * sirku 70/62 mm, fotka ve dvousloupci 62/58 mm. Mimo byt plati puvodni
     * tabulka beze zmeny.
     */
    if (flat && variant === 'panorama')
        return audience === 'klient' ? 70 : 62;
    if (flat && variant === 'plain')
        return audience === 'klient' ? 62 : 58;
    switch (variant) {
        case 'panorama':
            return audience === 'klient' ? 52 : 96;
        case 'zoning':
            return 84;
        case 'utilityMap':
            return 72;
        case 'visualisation':
            return 112;
        default:
            // `.plain` nema pevnou vysku — obrazek si drzi pomer stran podkladu
            // (1200 × 900 px, `MapRow`), tedy tri ctvrtiny sirky sloupce.
            return 0;
    }
}
/** Vnejsi okraje obrazkovych bloku, ktere pridava klientske CSS. */
function figureMarginMm(variant, audience, flat = false) {
    /** Krok REPB1: u bytu ma fotka pres celou sirku 3mm pod sebou (`.doc .pano`). */
    if (flat)
        return variant === 'panorama' ? 3 : 0;
    if (audience !== 'klient')
        return 0;
    if (variant === 'visualisation')
        return 4; // `.fig-viz{margin:2mm 0}`
    if (variant === 'panorama')
        return 5; // `.doc .pano{margin:2mm 0 3mm}`
    return 0;
}
/**
 * Velikost pisma tabulky. `.t` ma 8.8pt, ale tabulka zdroju nese i tridu `src`
 * — a `.src{font-size:7.4pt}` v `css.ts` plati i na ni (je to selektor tridy,
 * ne jen na `span`). Sazec pocita s tim, co se opravdu vysazi.
 */
function tableFontPt(variant) {
    /** Krok REPB1: huste tabulky reportu bytu maji vlastni velikost (`css.ts`). */
    const flat = {
        areas: PT.table,
        requests: PT.flatRequests,
        notChecked: PT.flatNotChecked,
        evidence: PT.flatEvidence,
    };
    return flat[variant] ?? (variant === 'sources' ? PT.source : PT.table);
}
/** Svisle odsazeni bunky podle varianty (`css.ts`). */
function tableCellPaddingMm(variant) {
    switch (variant) {
        case 'keyValueBig':
            return 2.8;
        case 'areas':
            return 1.7;
        case 'requests':
            return 1.15;
        case 'notChecked':
            return 1.4;
        case 'evidence':
            return 0.9;
        default:
            return 2;
    }
}
/**
 * Vyska obsahu bunky. V hustych tabulkach reportu bytu je `em` DRUHY RADEK
 * (zdroj pod nazvem oblasti, `css.ts`: `.t.areas em{display:block}`), takze se
 * meri zvlast a mensim pismem — jinak by sazec cekal jeden radek tam, kde jsou
 * dva, a strana by pretekla.
 */
function cellContentMm(value, widthMm, sizePt, blockEm) {
    const badgeMm = value.pill === null ? 0 : textWidthMm(value.pill.label, PT.pillSmall, 'sans600') + 4.8 + 1;
    const width = Math.max(widthMm - badgeMm, widthMm * 0.5) * (blockEm ? FLAT_TABLE_SAFETY : 1);
    if (!blockEm) {
        const plain = value.text
            .map((run) => (typeof run === 'string' ? run : 'b' in run ? run.b : run.em))
            .join('');
        return lineCount(plain, width, sizePt, 'sans400') * singleLineMm(sizePt);
    }
    const inline = value.text.filter((run) => typeof run === 'string' || 'b' in run);
    const blocks = value.text.filter((run) => typeof run !== 'string' && 'em' in run);
    /**
     * Krok REPB1: TUCNY BEH JE SIRSI. Prvni sloupec hustych tabulek je cely
     * tucne (nazev oblasti, otazka), takze mereni beznym rezem tvrdilo o radek
     * min — a strana 9 reportu Lyse o ten radek pretekla. `richLineCount` uz
     * s tucnym rezem pocita, takze se jen pouzije misto `lineCount`.
     */
    const inlineMm = inline.length === 0 ? 0 : richLineCount(inline, width, sizePt) * singleLineMm(sizePt);
    const blockMmTotal = blocks.reduce((sum, run) => sum + lineCount(run.em, width, PT.flatTableNote, 'sans400') * PT.flatTableNote * 1.4 * MM_PER_PT, 0);
    return Math.max(inlineMm + blockMmTotal, singleLineMm(sizePt));
}
/**
 * Krok REPB1 — REZERVA SIRKY V HUSTYCH TABULKACH BYTU.
 *
 * Duvody v tabulce „Co report neprověřil" jsou vety se spojovniky a tecnymi
 * oddelovaci („IPR Praha — územní plán · Národní geoportál …"). Chromium v nich
 * lame JINAK nez hladovy algoritmus nad mezerami: zmereno 3. 10. 2026 nad
 * stejnym retezcem — ve sloupci 66 mm vyjdou 3 radky, v sirsim 70 mm ctyri.
 * Mereni proto u techto tabulek pocita se sloupcem o 4 % uzsim; chyba tim jde
 * na stranu VETSI vysky, coz je jediny bezpecny smer (viz hlavicka souboru).
 * Puvodni mereni strany 9 reportu Lyse bylo o 0,9 mm kratsi, nez co prohlizec
 * vysadil, a strana tise orezavala.
 */
const FLAT_TABLE_SAFETY = 0.96;
/** Meri se `em` v teto tabulce jako vlastni radek? (Jen huste tabulky bytu.) */
function tableBlockEm(variant) {
    return (variant === 'areas' ||
        variant === 'requests' ||
        variant === 'notChecked' ||
        variant === 'evidence');
}
/**
 * Krok REPB1 — SIRKY SLOUPCU HUSTYCH TABULEK BYTU.
 *
 * Pomerne deleni (viz `tableColumnsMm`) u nich PODHODNOCOVALO vysku: tabulka
 * „Co report neprověřil" vysla o 4,3 mm nizsi, nez ji vysadil prohlizec, a
 * strana 9 reportu Lyse o 0,9 mm pretekla. Prohlizec totiz kazdemu sloupci
 * necha aspon NEJDELSI SLOVO a teprve zbytek deli podle obsahu — siroky
 * sloupec tak dostane MENE, nez by mu pomer dal, a lame se vic.
 *
 * Plati jen pro varianty, ktere zavedl krok REPB1 (`areas`, `requests`,
 * `notChecked`, `evidence`). Ostatni tabulky si drzi puvodni vypocet, aby se
 * schvalene strany pozemku a domu nehnuly ani o milimetr.
 */
function flatColumnsMm(rows, head, variant, maxima, usable) {
    const sizePt = tableFontPt(variant);
    const minima = maxima.map((_, index) => columnMinContentMm(rows, head, index, sizePt));
    const minTotal = minima.reduce((sum, value) => sum + value, 0);
    if (minTotal >= usable) {
        return minima.map((value) => (value / minTotal) * usable);
    }
    const slack = maxima.map((value, index) => Math.max(0, value - (minima[index] ?? 0)));
    const slackTotal = slack.reduce((sum, value) => sum + value, 0);
    if (slackTotal === 0)
        return maxima.map(() => usable / Math.max(maxima.length, 1));
    const rest = usable - minTotal;
    return minima.map((value, index) => value + (rest * (slack[index] ?? 0)) / slackTotal);
}
/** Nejdelsi NEDELITELNY obsah sloupce — pod nej prohlizec sloupec nestlaci. */
function columnMinContentMm(rows, head, index, sizePt) {
    let min = head === null ? 0 : longestWordMm(head[index] ?? '', PT.tableHead, 'sans600');
    for (const row of rows) {
        const value = row[index];
        if (value === undefined)
            continue;
        const badgeMm = value.pill === null ? 0 : textWidthMm(value.pill.label, PT.pillSmall, 'sans600') + 4.8 + 1;
        for (const run of value.text) {
            const text = typeof run === 'string' ? run : 'b' in run ? run.b : run.em;
            const face = typeof run !== 'string' && 'b' in run ? 'sans600' : 'sans400';
            min = Math.max(min, badgeMm + longestWordMm(text, sizePt, face));
        }
    }
    return min;
}
function longestWordMm(value, sizePt, face) {
    let max = 0;
    for (const word of value.split(' '))
        max = Math.max(max, textWidthMm(word, sizePt, face));
    return max;
}
/** Nejsirsi mozny obsah sloupce tabulky (prohlizec z toho pocita automaticke sirky). */
function columnMaxContentMm(rows, head, index, sizePt) {
    let max = head === null ? 0 : textWidthMm(head[index] ?? '', PT.tableHead, 'sans600');
    for (const row of rows) {
        const value = row[index];
        if (value === undefined)
            continue;
        const badgeMm = value.pill === null ? 0 : textWidthMm(value.pill.label, PT.pillSmall, 'sans600') + 4.8 + 1;
        const plain = value.text
            .map((run) => (typeof run === 'string' ? run : 'b' in run ? run.b : run.em))
            .join('');
        max = Math.max(max, badgeMm + textWidthMm(plain, sizePt, 'sans400'));
    }
    return max;
}
/**
 * Sirky sloupcu tabulky. Kdyz se nejsirsi obsah vejde cely, prohlizec sloupce
 * roztahne a ZADNA bunka se nelame — pak je jedno, jak presne. Kdyz se nevejde,
 * rozdeli se sirka v pomeru nejsirsiho obsahu (to je to, co automaticke
 * rozvrzeni tabulky delá priblizne taky).
 */
function tableColumnsMm(rows, head, variant, widthMm) {
    const count = Math.max(...rows.map((row) => row.length), head?.length ?? 0);
    const padding = 1.5; // `.t td{padding-right:1.5mm}`
    const usable = widthMm - count * padding;
    if (variant === 'keyValue' || variant === 'keyValueBig') {
        // `.t.kv td:first-child{width:36%}`
        const first = widthMm * 0.36 - padding;
        const rest = (usable - first) / Math.max(count - 1, 1);
        return [first, ...Array.from({ length: count - 1 }, () => rest)];
    }
    const maxima = Array.from({ length: count }, (_, index) => columnMaxContentMm(rows, head, index, tableFontPt(variant)));
    if (tableBlockEm(variant)) {
        return flatColumnsMm(rows, head, variant, maxima, usable);
    }
    const total = maxima.reduce((sum, value) => sum + value, 0);
    if (total === 0)
        return maxima.map(() => usable / count);
    // Tabulka ma `width:100%`: kdyz se nejsirsi obsah vejde, prohlizec zbylou
    // sirku ROZDELI mezi sloupce a zadna bunka se nelame. Rozdeleni v pomeru
    // nejsirsiho obsahu je totez, co dela automaticke rozvrzeni tabulky.
    return maxima.map((value) => (value / total) * usable);
}
function tableMm(rows, head, variant, widthMm) {
    const columns = tableColumnsMm(rows, head, variant, widthMm);
    const cellPadding = tableCellPaddingMm(variant);
    const sizePt = tableFontPt(variant);
    let total = 0;
    if (head !== null) {
        const headLines = Math.max(1, ...head.map((label, index) => lineCount(label, columns[index] ?? widthMm, PT.tableHead, 'sans600')));
        total += 1.6 * 2 + headLines * singleLineMm(7.8) + 2 * PX_MM;
    }
    const blockEm = tableBlockEm(variant);
    for (const row of rows) {
        const contentMm = Math.max(singleLineMm(sizePt), ...row.map((value, index) => cellContentMm(value, columns[index] ?? widthMm, sizePt, blockEm)));
        total += cellPadding * 2 + contentMm + PX_MM;
    }
    return total;
}
/** Vyska jedne dlazdice `.tile` v mrizce dane sirky. */
function tileMm(ctx, tile, widthMm, small) {
    const labelPt = small ? PT.tileLabelFour : PT.labelSmall;
    const notePt = small ? PT.tileNoteFour : PT.tileNote;
    let inner = lineCount(tile.label, widthMm, labelPt, 'sans400') * singleLineMm(labelPt);
    if (tile.gauge !== null) {
        // Stupnice ma sirku sloupce a pomer stran podle poctu pasem (`gaugeSvg`).
        inner += chartSvgMm(gaugeSvg(tile.gauge.level, tile.gauge.levels, ctx.chart.palette), widthMm);
    }
    else if (tile.value !== null) {
        inner += lineCount(tile.value, widthMm, PT.tileValue, 'sans600') * PT.tileValue * LINE_HEIGHT * MM_PER_PT;
    }
    inner += 0.8 + lineCount(tile.text, widthMm, notePt, 'sans400') * notePt * 1.4 * MM_PER_PT;
    return 2.3 * 2 + inner + PX_MM;
}
/** Vyska mrizky dlazdic: soucet nejvyssi dlazdice v kazde rade. */
function tilesMm(ctx, items, widthMm) {
    const four = items.length > 9;
    const columns = four ? 4 : 3;
    const gap = four ? 4 : 6;
    const cell = (widthMm - (columns - 1) * gap) / columns;
    let total = 2 * PX_MM; // `border-top:2px`
    for (let start = 0; start < items.length; start += columns) {
        const row = items.slice(start, start + columns);
        total += Math.max(...row.map((tile) => tileMm(ctx, tile, cell, four)));
    }
    return total;
}
/**
 * Vyska otisku kontrol: mrizka „skupina | dlazdice", kde se dlazdice zalamuji.
 * Sirka prave bunky je 180 − 24 mm (`css.ts`: `.orow{grid-template-columns:24mm 1fr}`).
 */
function imprintMm(ctx) {
    const counts = { ok: 0, attention: 0, pending: 0, unavailable: 0 };
    for (const group of ctx.model.checkImprint.groups) {
        for (const item of group.items)
            counts[item.status] += 1;
    }
    const summary = t('dd.reportV5.imprint.counts', {
        locale: ctx.locale,
        values: { ...counts, revision: ctx.model.identity.revision + 1 },
    });
    let total = textBlockMm(summary, CONTENT_WIDTH_MM, PT.small, 'sans400') + 2.5; // `<p class="small mute">`
    total += 2 * PX_MM; // `.otisk{border-top:2px}`
    const cellWidth = CONTENT_WIDTH_MM - 24;
    for (const group of ctx.model.checkImprint.groups) {
        // `.oc{display:flex;flex-wrap:wrap;gap:1.4mm 3mm}`, dlazdice = ikona 3.4mm + 1.4mm + text.
        let rows = 1;
        let used = 0;
        for (const item of group.items) {
            const itemMm = 3.4 + 1.4 + textWidthMm(item.label, PT.imprintItem, 'sans400');
            if (used === 0)
                used = itemMm;
            else if (used + 3 + itemMm <= cellWidth)
                used += 3 + itemMm;
            else {
                rows += 1;
                used = itemMm;
            }
        }
        const cellsMm = rows * singleLineMm(PT.imprintItem) + (rows - 1) * 1.4;
        const groupMm = singleLineMm(PT.imprintGroup);
        total += 2 * 2 + Math.max(cellsMm, groupMm) + PX_MM;
    }
    return total;
}
/**
 * Vyska bloku v dane sirce sloupce, VCETNE jeho vlastnich vnejsich okraju.
 * `compact` = kapitola sazi otazky hustej (ctyri a vic otazek, viz `css.ts`).
 */
export function blockMm(ctx, item, widthMm = CONTENT_WIDTH_MM, compact = false) {
    switch (item.kind) {
        case 'paragraph': {
            const sizePt = item.small ? PT.small : PT.body;
            return richTextMm(item.text, widthMm, sizePt) + 2.5;
        }
        case 'subheading':
            return 5 + textBlockMm(item.text, widthMm, PT.h3, 'sans600') + 2.5;
        case 'question': {
            // `.q{grid-template-columns:1fr 44mm;gap:6mm;padding:3.4mm 0;border-bottom:1px}`
            /**
             * Krok REPB1 — OTAZKY MAKLERSKE VERZE BYTU SE SAZI HUSTEJI (predloha):
             * sloupec odpovedi je 40 mm, nadpis 9.6pt a odůvodnění 8.3pt/1.42.
             * Ctyri cisla, ktera musi sedet s `css.ts` — jinak sazec meri jiny
             * dokument, nez se vytiskne.
             */
            const flatDetailed = isFlat(ctx) && ctx.audience === 'podrobny';
            const railMm = flatDetailed ? 40 : 44;
            const left = Math.max(widthMm - railMm - 6, 20);
            const questionMm = flatDetailed
                ? textBlockMm(item.question, left, PT.flatQuestionHeading, 'sans600') + 0.8
                : textBlockMm(item.question, left, PT.h4, 'sans600') + 1;
            const basisText = basisWithEvidence(item);
            const basisMm = basisText === ''
                ? 0
                : flatDetailed
                    ? textBlockMm(basisText, left, PT.flatQuestionBasis, 'sans400', FLAT_QUESTION_LINE_HEIGHT)
                    : textBlockMm(item.basis, left, PT.questionBasis, 'sans400');
            /**
             * `.pill` (9.4pt, `padding:1.3mm 3.2mm`) + `.src` (7.4pt), mezi nimi `gap:1.3mm`.
             *
             * Krok REP10 — PILULKA SE LAME. Do REP10 se pocitala jako JEDEN radek
             * (`singleLineMm`), protoze odpovedi pozemku jsou kratke („v dosahu",
             * „plán existuje"). Odpovedi bytu jsou vety („na jednotce je zapsané
             * zástavní právo; před převodem je potřeba doklad o jeho uvolnění") a ve
             * sloupci 44 mm zaberou ctyri radky — podrobny report bytu proto PRETEKAL
             * stranu o 51 mm a `.page{overflow:hidden}` by obsah tise oriznul.
             * Pro jednoradkovou odpoved vraci `textBlockMm` tutez hodnotu jako
             * `singleLineMm`, takze sazba pozemku se nemeni (doklad: snapshot
             * Umyslovic je pred i po shodny).
             */
            const railHeight = 1.3 * 2 +
                textBlockMm(item.verdict, railMm - 3.2 * 2, PT.pill, 'sans600') +
                1.3 +
                textBlockMm(item.source, railMm, PT.source, 'sans400');
            const padding = QUESTION_PADDING_MM[compact || flatDetailed ? 'compact' : 'normal'];
            return padding * 2 + Math.max(questionMm + basisMm, railHeight) + PX_MM;
        }
        case 'means': {
            // `.mean{margin:3.5mm 0;padding:3.4mm 4.5mm;border-left:1.4mm;font-size:9.5pt;line-height:1.5}`
            const inner = widthMm - 1.4 - 9;
            return (3.5 * 2 +
                3.4 * 2 +
                singleLineMm(PT.meansLabel, BODY_LINE_HEIGHT) +
                0.6 +
                textBlockMm(item.text, inner, PT.meansText, 'sans400', 1.5));
        }
        case 'pending': {
            const entry = ctx.model.pending.find((row) => row.id === item.pendingId);
            if (entry === undefined)
                return 0;
            // `.later{margin:2mm 0;padding:2.4mm 4mm;border-left:1.2mm;font-size:8.6pt}`
            const inner = widthMm - 1.2 - 8;
            const label = t('dd.reportV5.block.pending', { locale: ctx.locale });
            const line = `${label}  ${entry.what} ${entry.reason} ${entry.source}`;
            return 2 * 2 + 2.4 * 2 + textBlockMm(line, inner, PT.pendingNote, 'sans400');
        }
        case 'chart': {
            const chart = ctx.model.charts[item.chartId];
            if (chart === undefined)
                return 0;
            const svg = renderChartSvg(chart, ctx.chart);
            // `.chart{margin:2mm 0 0}`, titulek `.ct` (margin-bottom 1.5mm), poznamka `.cn`.
            return (2 +
                textBlockMm(item.title, widthMm, PT.chartTitle, 'sans600') +
                1.5 +
                chartSvgMm(svg, widthMm) +
                (item.note === null ? 0 : 1 + textBlockMm(item.note, widthMm, PT.chartNote, 'sans400')));
        }
        case 'figure': {
            const image = ctx.model.images[item.imageId];
            if (image === undefined)
                return 0;
            const caption = item.caption ?? image.caption;
            const imageMm = figureImageMm(item.variant, ctx.audience, isFlat(ctx)) || widthMm * 0.75;
            return (figureMarginMm(item.variant, ctx.audience, isFlat(ctx)) +
                imageMm +
                1.6 +
                textBlockMm(caption, widthMm, PT.caption, 'sans400', 1.45));
        }
        case 'imageStrip': {
            const frames = item.imageIds
                .map((id) => ctx.model.images[id])
                .filter((image) => image !== undefined);
            if (frames.length === 0)
                return 0;
            // `.hist{grid-template-columns:repeat(4,1fr);gap:3mm}`
            const cell = (widthMm - 3 * 3) / 4;
            const imageMm = item.size === 'big' ? 84 : 46;
            const captionMm = Math.max(...frames.map((image) => textBlockMm(image.badge ?? image.caption, cell, PT.imageStripCaption, 'sans600', 1.45)));
            const stripMarginMm = ctx.audience === 'klient' && item.size === 'big' ? 4 : 0;
            const noteMm = item.note === null ? 0 : textBlockMm(item.note, widthMm, PT.small, 'sans400') + 2.5;
            /*
             * Krok PROP7B — PAS MA TOLIK RADKU, KOLIK MU MRIZKA DA. Mrizka ma ctyri
             * sloupce, takze pet a vic ramecku zalomi dalsi radek (`gap:3mm` plati
             * i mezi radky). Odhad do PROP7B pocital VZDY jeden radek, takze pas
             * s sesti fotkami (zadani PROP7B bod 1) nebo s peti archivnimi ortofoty
             * byl pro sazece o cely radek nizsi, nez jak se vykresli — a strana
             * pretekla. Jeden radek vychazi stejne jako dosud, takze pasy do ctyr
             * ramecku stránkovani nemeni.
             */
            const rows = Math.ceil(frames.length / 4);
            return stripMarginMm + rows * (imageMm + 1 + captionMm) + (rows - 1) * 3 + noteMm;
        }
        case 'kpis': {
            // `.kpis{grid-template-columns:repeat(4,1fr);border-top:2px;margin:0 0 4mm}`
            /** Krok REPB1: dvojice dlazdic u bytu jde na dva sloupce (`.kpis.two`). */
            const columnCount = kpisTwoColumns(ctx, item.items.length) ? 2 : 4;
            const cell = widthMm / columnCount - 2;
            let total = 2 * PX_MM + 4;
            for (let start = 0; start < item.items.length; start += columnCount) {
                const row = item.items.slice(start, start + columnCount);
                total += Math.max(...row.map((kpi) => 2.8 * 2 +
                    lineCount(kpi.label, cell, PT.labelSmall, 'sans400') * singleLineMm(PT.labelSmall) +
                    lineCount(kpi.value, cell, PT.kpiValue, 'serif600') * PT.kpiValue * 1.2 * MM_PER_PT +
                    (kpi.note === null
                        ? 0
                        : lineCount(kpi.note, cell, PT.kpiNote, 'sans400') * singleLineMm(PT.kpiNote)) +
                    PX_MM));
            }
            return total;
        }
        case 'tiles':
            return tilesMm(ctx, item.items, widthMm);
        case 'table':
            return tableMm(item.rows, item.head, item.variant, widthMm);
        case 'timeline': {
            // `.timeline{border-top:2.5px;margin:4mm 0 0}`, `.tl{flex:1;padding:3mm 4mm 0 0}`
            const cell = widthMm / item.items.length - 4;
            const inner = Math.max(...item.items.map((entry) => lineCount(entry.year, cell, PT.timelineYear, 'serif600') * PT.timelineYear * LINE_HEIGHT * MM_PER_PT +
                lineCount(entry.title, cell, PT.body, 'sans600') * singleLineMm(PT.body) +
                lineCount(entry.note, cell, PT.timelineNote, 'sans400') * singleLineMm(PT.timelineNote)));
            return 4 + 2.5 * PX_MM + 3 + inner;
        }
        case 'checkboxList': {
            // `.checks li{padding:1.2mm 0 1.2mm 6mm;border-bottom:1px}`
            /** Krok REPB1: v reportu bytu je seznam k doložení mensim pismem (8.8pt). */
            const sizePt = isFlat(ctx) ? PT.checksFlat : PT.body;
            let total = 0;
            for (const entry of item.items) {
                total += 1.2 * 2 + richTextMm(entry, widthMm - 6, sizePt) + PX_MM;
            }
            return total;
        }
        case 'orderedList': {
            if (item.variant === 'steps') {
                // `.steps{padding-left:5mm} li{margin-bottom:2.5mm}`
                let total = 0;
                for (const entry of item.items) {
                    total +=
                        richTextMm(entry.text, widthMm - 5, PT.body) +
                            (entry.note === null ? 0 : textBlockMm(entry.note, widthMm - 5, PT.small, 'sans400')) +
                            2.5;
                }
                return total;
            }
            // `.road{padding-left:6mm;margin:3mm 0 0} li{padding:2mm 0;border-bottom:1px}`
            let total = 3;
            for (const entry of item.items) {
                total +=
                    2 * 2 +
                        richTextMm(entry.text, widthMm - 6, PT.body) +
                        (entry.note === null ? 0 : textBlockMm(entry.note, widthMm - 6, PT.small, 'sans400')) +
                        PX_MM;
            }
            return total;
        }
        case 'bulletList': {
            // `.asks{padding-left:5mm} li{margin-bottom:1.6mm}`
            let total = 0;
            for (const entry of item.items)
                total += richTextMm(entry, widthMm - 5, PT.body) + 1.6;
            return total;
        }
        case 'verdict': {
            // `.verdict{padding:4mm 5mm;margin-bottom:2mm;gap:5mm}` + kolecko 11 mm + slovo stavu pod nim
            const inner = widthMm - 10 - 11 - 5;
            return (4 * 2 +
                Math.max(VERDICT_MARK_MM, textBlockMm(ctx.model.verdict.summary, inner, PT.verdictText, 'sans400', 1.5)) +
                2);
        }
        case 'imprint':
            return imprintMm(ctx);
        case 'sourcesTable': {
            const head = [
                t('dd.reportV5.table.sources.what', { locale: ctx.locale }),
                t('dd.reportV5.table.sources.source', { locale: ctx.locale }),
                t('dd.reportV5.table.sources.asOf', { locale: ctx.locale }),
            ];
            const rows = ctx.model.sources.map((row) => [
                plainCell(row.what),
                plainCell(row.source),
                plainCell(localeDate(row.asOf, ctx.locale)),
            ]);
            return tableMm(rows, head, 'sources', widthMm);
        }
        case 'pendingTable': {
            const head = [
                t('dd.reportV5.table.pending.what', { locale: ctx.locale }),
                t('dd.reportV5.table.pending.reason', { locale: ctx.locale }),
            ];
            const rows = ctx.model.pending.map((row) => [plainCell(row.what), plainCell(row.reason)]);
            return tableMm(rows, head, 'plain', widthMm);
        }
        case 'columns': {
            // `.grid2{grid-template-columns:1fr 1fr;gap:7mm}`, `.w55{1.15fr 1fr}`
            const usable = widthMm - 7;
            const leftMm = item.ratio === 'wide-left' ? (usable * 1.15) / 2.15 : usable / 2;
            const rightMm = usable - leftMm;
            return Math.max(columnStackMm(ctx, item.left, leftMm, compact), columnStackMm(ctx, item.right, rightMm, compact));
        }
        case 'contact': {
            /**
             * `.contact{border:2px;padding:5mm 6mm;margin-top:auto;gap:6mm}` + znacka
             * 12 mm. Krok REPORT1B: `margin-top` je `auto` (stejne jako `.legal` o pár
             * řádků niz) — box se tim neda spocitat jako pevnych 6 mm navic, protoze
             * tolik mista zabere jen tehdy, kdyz je strana prave tak plna; jinak ho
             * flex sloupec dotáhne k paticce sam. Merí se proto jen vlastni obsah
             * boxu, presne jako u `.legal`.
             */
            const inner = widthMm - 12 - 12 - 6 - 4 * PX_MM;
            const office = ctx.brand?.officeName ?? null;
            const address = ctx.brand?.footerText ?? null;
            const contact = ctx.brand?.contactLine ?? null;
            const extra = [[office, address].filter((v) => v !== null).join(', '), contact ?? ''].filter((line) => line !== '');
            const textMm = richTextMm(item.text, inner, PT.contact) +
                extra.reduce((sum, line) => sum + textBlockMm(line, inner, PT.contact, 'sans400'), 0);
            return 5 * 2 + 2 * 2 * PX_MM + Math.max(12, textMm);
        }
        case 'legal':
            return textBlockMm(item.text, widthMm, PT.small, 'sans400') + 2.5;
    }
}
/** Odsazeni otazky podle toho, jestli kapitola sazi otazky hustej (`css.ts`). */
const QUESTION_PADDING_MM = { normal: 3.4, compact: 2.6 };
/**
 * Krok REPB1: odůvodnění vcetne stitku dukazu. Stitek je `inline-block`
 * s vlastnim ramečkem, takze radek prodluzuje — mereni ho musi videt.
 */
function basisWithEvidence(item) {
    return item.evidence.length === 0 ? item.basis : `${item.basis} ${item.evidence.join(' ')}`;
}
function plainCell(value) {
    return { text: [value], pill: null, align: 'start' };
}
/** Datum v jazyce dokumentu — tytez pravidla jako v sablone (`template.ts`). */
export function localeDate(iso, locale) {
    const parsed = new Date(iso);
    if (Number.isNaN(parsed.getTime()))
        return iso;
    return parsed.toLocaleDateString(locale, { day: 'numeric', month: 'numeric', year: 'numeric' });
}
/**
 * Sloupec uvnitr `.grid2` je BEZNY tok, ne flex — sousedni svisle okraje se
 * tam SKLADAJI (na rozdil od `main`). Proto se scitaji vysky bez okraju a
 * mezi bloky se bere jen ten vetsi z dvojice.
 */
function columnStackMm(ctx, blocks, widthMm, compact) {
    let total = 0;
    for (const item of blocks)
        total += blockMm(ctx, item, widthMm, compact);
    // Sklady okraju uberou nejvyse jeden okraj mezi kazdou dvojici; bezpecny
    // smer je NEODECITAT nic (vyjde vyssi sloupec, tedy opatrnejsi sazba).
    return total;
}
//# sourceMappingURL=estimate.js.map